import { queue, AsyncQueue as AsyncQueueT } from "async";

export interface QueueTask<T = any> {
  id: string;
  operation: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
}

export class AsyncQueue<T = any> {
  private queue: AsyncQueueT<QueueTask<T>>;
  private concurrency: number;

  constructor(concurrency: number = 1) {
    this.concurrency = concurrency;
    this.queue = queue<QueueTask<T>>((task, callback) => {
      task
        .operation()
        .then((result) => {
          task.resolve(result);
          callback();
        })
        .catch((error) => {
          task.reject(error);
          callback();
        });
    }, concurrency);
  }

  async push<R = T>(operation: () => Promise<R>, id?: string): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      const task: QueueTask<R> = {
        id: id || Date.now().toString(),
        operation,
        resolve,
        reject,
      };

      this.queue.push(task as any);
    });
  }

  async unshift<R = T>(operation: () => Promise<R>, id?: string): Promise<R> {
    return new Promise<R>((resolve, reject) => {
      const task: QueueTask<R> = {
        id: id || Date.now().toString(),
        operation,
        resolve,
        reject,
      };

      this.queue.unshift(task as any);
    });
  }

  async drain(): Promise<void> {
    return new Promise((resolve) => {
      if (this.queue.length() === 0 && this.queue.idle()) {
        resolve();
        return;
      }
      this.queue.drain(() => {
        resolve();
      });
    });
  }

  onError(callback: (error: any, task: QueueTask<T>) => void): void {
    this.queue.error(callback);
  }

  get length(): number {
    return this.queue.length();
  }

  get running(): number {
    return this.queue.running();
  }

  get idle(): boolean {
    return this.queue.idle();
  }

  pause(): void {
    this.queue.pause();
  }

  resume(): void {
    this.queue.resume();
  }

  kill(): void {
    this.queue.kill();
  }

  remove(id: string): QueueTask<T>[] {
    return this.queue.remove((task) => (task as any).id === id) as any;
  }
}

export function createAsyncQueue<T = any>(
  concurrency: number = 1,
): AsyncQueue<T> {
  return new AsyncQueue<T>(concurrency);
}
